import { useMemo, useCallback } from "react"
import _ from "lodash"

interface Car {
  make: {
    name: string
    slug: string
  }
  model: {
    name: string
    slug: string
  }
  trim?: {
    name: string
    slug: string
  }
  years: string
}

interface UseVehicleSelectionHandlersProps {
  cars: Car[]
  selectedMake: string
  selectedModel: string
  selectedTrim: string
  setMake: (make: string) => void
  setModel: (model: string) => void
  setTrim: (trim: string) => void
  setYear: (year: string) => void
  setModels: (models: Array<{ label: string; value: string }>) => void
  setTrims: (trims: Array<{ label: string; value: string }>) => void
  setYears: (years: Array<{ label: string; value: string }>) => void
  isModern?: boolean // Flag to determine if we should aggregate years from multiple cars
}

export function useVehicleSelectionHandlers({
  cars,
  selectedMake,
  selectedModel,
  selectedTrim,
  setMake,
  setModel,
  setTrim,
  setYear,
  setModels,
  setTrims,
  setYears,
  isModern = false,
}: UseVehicleSelectionHandlersProps) {
  // Generate makes list
  const makes = useMemo(
    () =>
      _.sortBy(
        _.uniqBy(
          cars
            .map(({ make }) => ({ label: make.name, value: make.slug }))
            .sort(),
          "value"
        ),
        ["value"]
      ) as Array<{ label: string; value: string }>,
    [cars]
  )

  // Handler for make selection
  const onSelectMake = useCallback(
    (make: string) => {
      const filteredModels = _.sortBy(
        _.uniqBy(
          cars
            .filter((car) => car.make.slug === make)
            .map(({ model }) => ({ label: model.name, value: model.slug })),
          "value"
        ),
        ["value"]
      )

      setMake(make)
      setModels(filteredModels)
      setModel("")
      setTrim("")
      setYear("")
      setTrims([])
      setYears([])
    },
    [cars, setMake, setModels, setModel, setTrim, setYear, setTrims, setYears]
  )

  // Handler for model selection
  const onSelectModel = useCallback(
    (model: string) => {
      if (model.trim() !== "") {
        const filteredCars = _.sortBy(
          cars.filter(
            (car) => car.make.slug === selectedMake && car.model.slug === model
          ),
          ["slug"]
        )

        if (filteredCars.length > 1 && filteredCars.some((car) => car.trim)) {
          // Show trims if multiple cars with trims
          const trims = _.sortBy(
            _.uniqBy(
              filteredCars
                .map(({ trim }) => ({
                  label: trim?.name,
                  value: trim?.slug,
                }))
                .filter((t) => t.value) as any,
              "value"
            ),
            ["value"]
          ) as any

          setModel(model)
          setTrim("")
          setTrims(trims)
          setYears([])
        } else {
          // Show years directly
          let filteredYears: Array<{ label: string; value: string }>

          if (isModern) {
            // Modern: Aggregate years from ALL matching cars
            filteredYears = _.sortBy(
              _.uniqBy(
                filteredCars
                  .flatMap((car) => car.years.split(","))
                  .map((year) => year.trim())
                  .filter((year) => year !== "")
                  .map((year) => ({
                    label: year,
                    value: year,
                  })),
                "value"
              ),
              [(item) => Number(item.value)]
            )
          } else {
            // Classic: Use years from first car (comma-space separated)
            const thisCar = filteredCars[0]
            filteredYears = thisCar.years.split(", ").map((year) => ({
              label: year.trim(),
              value: year.trim(),
            }))
          }

          setModel(model)
          setYear("")
          setTrim("")
          setTrims([])
          setYears(filteredYears)
        }
      } else {
        setModel("")
        setTrims([])
        setYears([])
      }
    },
    [
      cars,
      selectedMake,
      isModern,
      setModel,
      setTrim,
      setYear,
      setTrims,
      setYears,
    ]
  )

  // Handler for trim selection
  const onSelectTrim = useCallback(
    (trim: string) => {
      const matchingCars = cars.filter((car) => {
        return (
          car.make.slug === selectedMake &&
          car.model.slug === selectedModel &&
          car.trim?.slug === trim
        )
      })

      if (matchingCars.length > 0) {
        let filteredYears: Array<{ label: string; value: string }>

        if (isModern) {
          // Modern: Aggregate years from all matching cars
          filteredYears = _.sortBy(
            _.uniqBy(
              matchingCars
                .flatMap((car) => car.years.split(","))
                .map((year) => year.trim())
                .filter((year) => year !== "")
                .map((year) => ({
                  label: year,
                  value: year,
                })),
              "value"
            ),
            [(item) => Number(item.value)]
          )
        } else {
          // Classic: Use years from first matching car
          const thisCar = matchingCars[0]
          filteredYears = thisCar.years.split(", ").map((year) => ({
            label: year.trim(),
            value: year.trim(),
          }))
        }

        setTrim(trim)
        setYears(filteredYears)
      }
    },
    [cars, selectedMake, selectedModel, isModern, setTrim, setYears]
  )

  return {
    makes,
    onSelectMake,
    onSelectModel,
    onSelectTrim,
  }
}
